import {
  createAction,
  isAnyOf,
} from '@reduxjs/toolkit'

import { FeatureKey } from '@/constants/feature-key'
import { NormalStatus } from '@/constants/normal-status'
import {
  MAX_PAGE_SIZE,
  Pagination,
} from '@/constants/pagination'
import { Creator } from '@/model/creator'
import { creatorSelector } from '@/redux/creator-adaptor'
import {
  createAppAsyncThunk,
  startAppListening,
} from '@/redux/utils'
import {
  axios,
  initBaeTokenMap,
} from '@/utils/axios'

export const creatorNamespace = 'creator'

export const setSelectedCreatorId = createAction<number | undefined>(`${creatorNamespace}/setSelectedCreatorId`)

export interface ReadAllCreatorsParams {
  organizationId?: number
  name?: string
  status?: NormalStatus
  permissionKey?: FeatureKey
}

export const readAllCreators = createAppAsyncThunk(
  `${creatorNamespace}/readAllCreators`,
  async (
    {
      name,
      status,
      permissionKey,
    }: ReadAllCreatorsParams | undefined = {},
    { getState },
  ) => {
    const { global } = getState()
    const finalPermissionKey = permissionKey || global.permissionKey
    const { data } = await axios.get<Pagination<Creator>>(
      '/creator/all', {
      params: {
        name,
        status,
        pageSize: MAX_PAGE_SIZE,
        permissionKey: finalPermissionKey,
      },
    })
    return { data, permissionKey: finalPermissionKey }
  },
)
export type CreateCreatorParams = Omit<Creator,
  'id' |
  'status' |
  'linkStatus' |
  'cookies' |
  'localStorage' |
  'unreadMessages' |
  'unreadNotifications' |
  'proxy'>
export const createCreator = createAppAsyncThunk(
  `${creatorNamespace}/createCreator`,
  async (params: CreateCreatorParams, { getState }) => {
    const { global: { permissionKey } } = getState()
    const { data } = await axios.post<Creator>(
      '/creator',
      params,
    )
    return { data, permissionKey }
  },
)
export type UpdateCreatorParams = Omit<Creator,
  'password' |
  'cookies' |
  'organizationId' |
  'localStorage' |
  'unreadMessages' |
  'unreadNotifications' |
  'proxy'>
export const updateCreator = createAppAsyncThunk(
  `${creatorNamespace}/updateCreator`,
  async (params: UpdateCreatorParams) => {
    const { data } = await axios.put<Creator>(
      `/creator/${params.id}`,
      params,
    )
    return data
  },
)
export const removeCreator = createAppAsyncThunk(
  `${creatorNamespace}/removeCreator`,
  async (id: number) => {
    await axios.delete(`/creator/${id}`)
  },
)
export const activateCreator = createAppAsyncThunk(
  `${creatorNamespace}/activateCreator`,
  async (id: number) => {
    const { data } = await axios.post<Creator>(`/creator/${id}/action/activate`)
    return data
  },
)
export const deactivateCreator = createAppAsyncThunk(
  `${creatorNamespace}/deactivateCreator`,
  async (id: number) => {
    const { data } = await axios.post<Creator>(`/creator/${id}/action/deactivate`)
    return data
  },
)
export const refreshCreatorTokens = createAppAsyncThunk(
  `${creatorNamespace}/refreshCreatorTokens`,
  async () => {
    await axios.get('/creator/token/freshAll')
  },
)
export interface RefreshCreatorAuthTokenParams {
  creatorId: number
  forceRefresh: boolean
}
export const refreshCreatorAuthToken = createAppAsyncThunk(
  `${creatorNamespace}/refreshCreatorAuthToken`,
  async ({
    creatorId,
    forceRefresh,
  }: RefreshCreatorAuthTokenParams, { getState }) => {
    const creator = creatorSelector.selectById(getState().creator.creatorEntity, creatorId)
    const result = await axios.post<{ token?: string }>('/creator/login', {
      user_id: creator.onlyFansUserId,
      creatorId,
      forceRefresh,
    })

    if (typeof result === 'string') {
      return {
        token: result,
        creatorId,
      }
    }
    return {
      token: result.data.token || '',
      creatorId,
    }
  },
)
startAppListening({
  matcher: isAnyOf(
    readAllCreators.fulfilled,
    createCreator.fulfilled,
  ),
  effect: (_, { getState }) => {
    creatorSelector.selectAll(getState().creator.creatorEntity).forEach(async ({ id, token }) => {
      initBaeTokenMap({ creatorId: id, token })
    })
  },
})
